import { Fragment, FunctionComponent, RefObject } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'preact/hooks';
import { CirclePlayPauseButton } from '../../circlePlayPauseButton/circlePlayPauseButton';
import { TopBar } from '../../topBar/topBar';
import { css } from '../../../../common/styles/styles.utils';
import { SeekBarContainer } from '../../seekBar/seekBarContainer';
import { ControlsBar } from '../../controlsBar/controlsBar';
import { getFullVideoControlsStyleObject } from './fullVideoControls.style';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { CallToActionButton } from '../../callToActionButton/callToActionButton';
import { callToActionSelector } from '../../../../common/store/selectors/callToAction.selector';
import { isEmpty, isEmptyObject } from '../../../../common/utils/utils';
import { videoDataSelector } from '../../../../common/store/selectors/videoData.selector';
import { resetCallToActionPerVideo, setCallToActionPerVideo } from '../../store/controlsPlugin.actions';
import { StoriesSeekBars } from '../../storiesSeekBars/storiesSeekBars';
import { recirculationVideoDataSelector } from '../../../../common/store/selectors/recirculationVideoData.selector';
import { adsDataSelector } from '../../../../common/store/selectors/adsData.selector';

const CTA_CUSTOM_PARAM_KEY = 'cta';
const URL_CUSTOM_PARAM_VALUE = 'url';
const BUTTON_TEXT_CUSTOM_PARAM_VALUE = 'text';

interface FullVideoControlsComponentVariablesProps {
  clickToPlayMode: boolean;
}

export const FullVideoControls: FunctionComponent<FullVideoControlsComponentVariablesProps> = props => {
  const { clickToPlayMode } = props;
  const dispatch = useDispatch();
  const showControls = useSelector(playerDataSelector.showControls);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const ctaURLPerPlayer = useSelector(callToActionSelector.urlPerPlayer);
  const ctaURLPerVideo = useSelector(callToActionSelector.urlPerVideo);
  const customParams = useSelector(videoDataSelector.customParams);
  const numberOfScenes = useSelector(recirculationVideoDataSelector.numberOfScenes);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);

  const ctaButtonElementRef: RefObject<HTMLDivElement> = useRef();
  const [ctaButtonWidth, setCtaButtonWidth] = useState<number>(0);

  const showAdControls = isVastAd && adBreak;
  const isControlsVisible = showControls && (!clickToPlayMode || showAdControls);
  const showCTA = !isEmpty(ctaURLPerPlayer) || !isEmpty(ctaURLPerVideo);
  const videoLoading = videoTagStatus === 'unloaded' || videoTagStatus === 'idle';
  const isRecirculationVideo = numberOfScenes !== 0;
  const showStoriesSeekBars = isRecirculationVideo && !videoLoading && !showAdControls && playerSize !== 'small' && playerSize !== 'extraSmall';

  const styles = getFullVideoControlsStyleObject(isControlsVisible, playerSize, isRecirculationVideo, showStoriesSeekBars, showAdControls, videoLoading);
  const { bottomControlsWrapperStyle, topControlsWrapperStyle } = styles;

  useEffect(() => {
    if (!isEmptyObject(customParams)) {
      let ctaValue;
      try {
        ctaValue = JSON.parse(customParams[CTA_CUSTOM_PARAM_KEY]);
      } catch {
        ctaValue = '';
      }
      const ctaButtonString = ctaValue?.[BUTTON_TEXT_CUSTOM_PARAM_VALUE];
      const ctaURL = ctaValue?.[URL_CUSTOM_PARAM_VALUE];
      if (!isEmpty(ctaURL) && !isEmpty(ctaButtonString)) {
        dispatch(setCallToActionPerVideo(ctaButtonString, ctaURL));
      }
    }
    return () => dispatch(resetCallToActionPerVideo());
  }, [customParams, dispatch]);

  return (
    <Fragment>
      <CirclePlayPauseButton />
      <div style={css(topControlsWrapperStyle)} onClick={(e): void => e.stopPropagation()}>
        { showStoriesSeekBars && <StoriesSeekBars /> }
        <TopBar clickToPlayMode={clickToPlayMode} ctaButtonWidth={ctaButtonWidth} showCTA={showCTA} />
        { showCTA && <CallToActionButton buttonRef={ctaButtonElementRef} setCtaButtonWidth={setCtaButtonWidth} />}
      </div>
      <div style={css(bottomControlsWrapperStyle)} onClick={(e): void => e.stopPropagation()}>
        <SeekBarContainer clickToPlayMode={clickToPlayMode} />
        <ControlsBar />
      </div>
    </Fragment>
  );
};
